/**
 * EVAS v3 — LeadVal INGESTION document contract (EVAS-139, Phase 4 of the LeadVal epic).
 *
 * This module is the wire layer of the Supermemory document LeadVal reads at its Call 1: the transcript
 * grammar, the sanitisation every user-authored string must pass through, and the `metadata` assembly.
 * Everything here is PURE — no I/O, no env — so the grammar can be unit-tested against the fixtures in
 * LeadVal's own spec. The writer that streams chunks lives in `@evas/agent-core`.
 *
 * Source of truth: `../LeadVal/docs/LEADVAL_INGESTION_SPEC.md` v1.2 (read 2026-09-03) — §3.1
 * single-stringification, §4 envelope, §5 grammar, §6 metadata, §7 fingerprint, §8 tool payloads,
 * Appendix A the store mapping. Our decisions: `docs/leadval/02_SUPERMEMORY_INGESTION_REPORT.md`;
 * the IP/geo half of the payload: `docs/leadval/12_LEADVAL_IP_GEO_CHANGE_REQUEST.md` ("What EVAS will
 * send today").
 *
 * Two properties of that spec shape almost every function below:
 *   1. **Nothing in the grammar is enforced by rejection** (§5.5) — a malformed turn still produces a
 *      lead, a *worse* one, and only a conformance note says so. So correctness here cannot be verified
 *      by "it worked"; it is verified by tests against the spec and by reading the status endpoint.
 *   2. **Four metadata keys ARE enforced by rejection** (§6): `sessionId`, `processor`, `started`,
 *      `embedding`. A full-vertical session whose document misses one creates NO lead and is *not*
 *      recoverable by fixing the connector afterwards — the session must be re-run. Hence
 *      {@link missingMandatoryMetadataKeys}: we check our own document before we send it.
 */
import { z } from "zod";
// ---------- endpoints ----------
/** Supermemory conversation upsert (the "supported document store", spec Appendix A.1). The pinned
 * `supermemory` SDK (4.24.12) has no `conversations` resource, so the writer calls this directly. */
export const SUPERMEMORY_CONVERSATIONS_URL = "https://api.supermemory.ai/v4/conversations";
/** Assembled-document read-back, for development verification only (Appendix A.3). */
export const SUPERMEMORY_DOCUMENTS_URL = "https://api.supermemory.ai/v3/documents";
/** LeadVal ingestion API base (spec §15.1). */
export const LEADVAL_API_BASE = "https://leadval.evas.live/api/v1";
// ---------- document envelope ----------
/** The exact turn separator a connector MUST emit (§5.1). The parser accepts a wider family — two or
 * more dashes with padding — which is a hazard to defend against, never a licence to vary
 * ({@link neutralizeTurnSeparator}). */
export const LEADVAL_TURN_SEPARATOR = "\n\n---\n\n";
/** `metadata.leadvalSpecVersion` wire value. **Not** the spec document's revision** — that is at 1.2
 * while the wire value has never moved off `"1.0"` (§6, and the callout that warns against mirroring
 * the document revision). Change only when their table says so. */
export const LEADVAL_SPEC_VERSION = "1.0";
/** Metadata keys the Signal Registry marks `MANDATORY` and that live IN `metadata` (§6). `content` and
 * the `customId`/`sessionId` agreement are also MANDATORY but ride the ENVELOPE and are validated by
 * their own rules — they are never reported in `rejection.missingMandatoryKeys`, so they are not here. */
export const LEADVAL_MANDATORY_METADATA_KEYS = [
    "sessionId",
    "processor",
    "started",
    "embedding",
];
/** How a session ended (§6.2). A real intent signal in their scoring — an abrupt `ERROR` ending is not
 * disengagement — so it is derived from the close reason, never guessed from the transcript. */
export const LEADVAL_END_REASONS = [
    "USER_LEFT",
    "AGENT_ENDED",
    "TIMEOUT",
    "TRANSFERRED",
    "ERROR",
    "UNKNOWN",
];
export const LeadvalEndReason = z.enum(LEADVAL_END_REASONS);
// ---------- sanitisation (§5.1, §5.5 — both are connector MUSTs) ----------
/**
 * Make a user-supplied claimed name safe to embed in a turn header.
 *
 * A header ends at the first `]` and a qualifier at the first `)`, and **the store passes both through
 * unescaped** (measured, §5.1). So `"Ken]dar"` yields a USER turn with no identity at all — speaker id
 * AND claim both `null` — and the header's remainder leaks into the turn body as text that then flows
 * into linguistic analysis as if the visitor had said it. `"` truncates the claim.
 *
 * Removing the three characters is the spec's own instruction ("strip or replace"), and an empty claim
 * is explicitly "treated as absent, which is correct and safe — prefer it to a half-escaped value", so
 * a name that was nothing but delimiters returns `undefined` rather than `""`.
 *
 * This is NOT the "no processed user text" prohibition (§5.4): that governs the *body*, which we send
 * verbatim. A header qualifier is connector-authored structure quoting a claim.
 */
export function sanitizeLeadvalClaimedName(name) {
    if (!name)
        return undefined;
    const cleaned = name
        .replace(/[\])"]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    return cleaned.length > 0 ? cleaned : undefined;
}
/**
 * Neutralise the turn-separator SHAPE in user-authored content. A connector **MUST** (§5.5).
 *
 * `content` is rendered verbatim, so a visitor who types the separator and a header gets a genuine,
 * fully-parsed turn: measured on the live store, one visitor message produced a second participant
 * (`[USER (spk:… "Marta Silva")]`) whose email and phone were extracted as contact vectors and attached
 * to the lead as a stakeholder — a visitor writing a third party's PII onto a lead we then sell. The
 * parser cannot fix it, because a separator we emitted and one the visitor typed are byte-identical.
 *
 * What counts as the shape is wider than `---`: **any run of two or more dashes alone on a line**, with
 * optional space/tab padding. We break the RUN by inserting a zero-width space (U+200B) after the first
 * dash, which is the spec's own second suggestion. Preferred over its first (`---` → `—`) because it
 * preserves every character the visitor actually typed — §1.1 wants user turns verbatim and this is the
 * one narrow exception, so it should be as narrow as possible. U+200B is not in JavaScript's `\s`, so
 * the dash-only-line pattern no longer matches.
 *
 * Deliberately broader than the parser: their boundary also requires blank lines around the dash line,
 * and we neutralise a dash-only line regardless of what surrounds it. A visitor's markdown rule mid-
 * paragraph is not a boundary today, but "no user-authored text can ever produce the boundary shape" is
 * the obligation, and the cost of over-reach is one invisible character inside a row of dashes.
 */
const ZERO_WIDTH_SPACE = "\u200b";
export function neutralizeTurnSeparator(text) {
    return text.replace(/^([ \t]*)(-)(-+)([ \t]*)$/gm, (_m, pad, first, rest, tail) => `${pad}${first}${ZERO_WIDTH_SPACE}${rest}${tail}`);
}
/**
 * Build a USER message's `name` — the whole qualifier, not just the `spk:` token (Appendix A.2).
 *
 * Ordering is not stylistic (§5.1): `spk:` MUST come first (`("Kendar" spk:abc…)` parses as a legacy
 * bare name and the speaker id is LOST), and the claim MUST be double-quoted and separated by
 * whitespace (without the space the quotes are swallowed into the speaker id, producing a diarization
 * identity that never matches `metadata.speakers`).
 *
 * **Always call this, even with no claimed name.** A bare `{role:"user"}` with no `name` renders as
 * `[USER]`, and once `metadata.speakers` declares one id, an id-less USER turn is WITHHELD from the
 * participant model along with every contact vector in it — a well-formed header, a 200 from the store,
 * and the only trace a `SPEAKER_ID_MISSING` note (§5.5, §6.4).
 */
export function leadvalUserMessageName(speakerId, claimedName) {
    const claim = sanitizeLeadvalClaimedName(claimedName);
    return claim ? `spk:${speakerId} "${claim}"` : `spk:${speakerId}`;
}
/**
 * Coerce a tool call's arguments or a tool result into the ONE JSON OBJECT §8 requires.
 *
 * A body that parses whole as a JSON **array** — the ordinary shape of a search result — is REFUSED:
 * retained raw, zero facts extracted, `NON_CONFORMANT_PAYLOAD` recorded. (Before that refusal existed a
 * bare array was graded as its *first element*, so row 1 of a result set moved the lead's price.) Scalars
 * are refused the same way. So arrays are wrapped as `{results: […]}` — the spec's own remedy — and
 * scalars as `{value: …}`.
 *
 * Text that is not JSON at all is wrapped as `{text: …}` rather than sent bare: their step 3 would scan
 * such a body for the first balanced `{…}` span, which on prose containing a brace produces a payload we
 * never meant to send. An object we author says exactly what we know.
 */
export function leadvalToolPayload(raw) {
    const value = typeof raw === "string" ? tryParseJson(raw) : raw;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
        return value;
    }
    if (Array.isArray(value))
        return { results: value };
    if (typeof raw === "string" && value === undefined)
        return { text: raw };
    return { value: value ?? null };
}
/** `undefined` = not JSON at all (distinct from a JSON `null`, which parses fine). */
function tryParseJson(text) {
    try {
        return JSON.parse(text);
    }
    catch {
        return undefined;
    }
}
// ---------- speaker ids (§6.4 — an ACCESS-CONTROL LIST, not a description) ----------
/**
 * The visitor's declared voice id for a session.
 *
 * ⚠ **This AMENDS the decision recorded in `docs/leadval/02` §6 Q2** ("we declare
 * `speakers: [{id: <participantTag>}]`"). That is not implementable: `speakers` is all-or-nothing from
 * the FIRST turn — once it declares one id, every USER turn without a declared id is withheld — while
 * `participantTag` is the `Participant.supermemoryUserTag`, which only exists after background
 * identification has run (seconds in, and sometimes never). Using it would either leave the first turns
 * un-tagged (withheld) or declare a second voice mid-session, which mints a second participant for one
 * person.
 *
 * A session-scoped id is also the *honest* value: LeadVal is explicit that a speaker id is a **voice**
 * identity, not a person ("the same human on two devices is two ids"), and we do not diarize at all —
 * no voice signature, by decision (`docs/leadval/09`). Claiming cross-session voice identity by reusing
 * our participant tag would assert something we never measured. Cross-session joining is LeadVal's own
 * job, from contact vectors and the fingerprint. The participant tag still travels, as an EVAS extra key.
 */
export function leadvalVisitorSpeakerId(sessionId) {
    return `spk_${sessionId.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`;
}
/** The manager's declared voice id, once a human joins (§6.4 + `docs/leadval/02` §6 Q2). Their turns
 * are genuine human utterances, so they are USER-role with their own declared id — undeclared would
 * withhold them, and folding them into the visitor's id would attribute a manager's words to the lead. */
export function leadvalManagerSpeakerId(userId) {
    return `mgr_${userId.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()}`;
}
// ---------- fingerprint enrichment (§7 + report 12 "What EVAS will send today") ----------
/**
 * Map our normalized `GeoIpResult` onto LeadVal's EXISTING `georesolver` key names (§7).
 *
 * Three deliberate choices, all promised to LeadVal in report 12 item 2:
 * - **`timestamp` is omitted.** Sending it would overwrite our fingerprint capture time in their
 *   evidence (`identity/evidence.ts:125`), and our capture time is the honest one. Revisit when R8.2
 *   lands.
 * - **`asn` is sent as their string form**, because that is what the key is on their side, even though
 *   MaxMind returns a number and IPinfo returns `"AS13335"`.
 * - **`regionCode` only when the provider gave an ISO subdivision CODE.** MaxMind returns
 *   `subdivision.iso_code`, but IPinfo's `region` is a NAME ("Virginia"), and our normalizer stores
 *   whichever it got in one field. Sending a name under a `…Code` key is a type lie, so a value that is
 *   not code-shaped is dropped rather than renamed.
 *
 * Not sent: `accuracyRadiusKm` and `timezone`. Both are open ASKS (R7.1/R7.2, EVAS-147), and every extra
 * key pays into their `hardwareEntropyDensityScore` (Shannon entropy over the whole flattened
 * fingerprint) which gates a suppression dimension — so unregistered keys are not free.
 *
 * **`isProxy` is three-valued (EVAS-147)**: `true` on any VPN/proxy/residential-proxy/Tor verdict,
 * `false` only when a vendor that can see VPNs and proxies answered (`anonymity.vendorChecked`) and found no
 * anonymity at all (no relay, no hosting), otherwise OMITTED. Their evidence reads it into
 * `network.anonymized`, where an absent flag stays `null`
 * and `false` is recorded as "the vendor checked and said no" (`../LeadVal`
 * `src/identity/evidence.ts:200-210`). The resolver's all-false default used to reach them as that `false`
 * whenever proxycheck timed out or ran out of quota.
 */
export function leadvalGeoresolver(geo) {
    const { geo: g, network, anonymity: a } = geo;
    const regionCode = isIsoSubdivisionCode(g.region) ? g.region : undefined;
    // A residential proxy is a proxy. MaxMind and IPinfo report it on its own axis, not under `isProxy`, and
    // with a vendor answering, leaving it out would send their "checked and clean" for one.
    const anonymized = a.isProxy || a.isVpn || a.isTor || a.isResidentialProxy === true;
    // "Checked and clean" also needs NO anonymity signal at all. A relay egress (an Apple user) or a datacenter
    // address is not a proxy, but it is not a located person either (R6.2's own argument), and MaxMind's
    // `is_anonymous` covers hosting. Neither bit is honest for them, so the key is omitted.
    const clean = a.vendorChecked === true && !a.isAnonymous && !a.isRelay && !a.isHosting;
    return {
        ipVersion: geo.ip.includes(":") ? 6 : 4,
        ipAddress: geo.ip,
        ...(g.lat !== undefined ? { latitude: g.lat } : {}),
        ...(g.lon !== undefined ? { longitude: g.lon } : {}),
        ...(g.country ? { countryCode: g.country } : {}),
        ...(regionCode ? { regionCode } : {}),
        ...(g.city ? { cityName: g.city } : {}),
        ...(g.postal ? { zipCode: g.postal } : {}),
        ...(network.asn !== undefined ? { asn: String(network.asn) } : {}),
        ...(network.asOrg ? { asnOrganization: network.asOrg } : {}),
        // Their single undifferentiated boolean (R7.3 asks for it to be re-specified): three-valued, see above.
        ...(anonymized ? { isProxy: true } : clean ? { isProxy: false } : {}),
    };
}
/** ISO 3166-2 subdivision codes are 1–3 alphanumerics after the country prefix (`WA`, `30`, `BC`).
 * Anything longer or containing a space is a region NAME. */
function isIsoSubdivisionCode(value) {
    return typeof value === "string" && /^[A-Z0-9]{1,3}$/.test(value);
}
/**
 * The anonymity verdict, at the path R6.1 proposes (`transientMetadata.ipIntelligence`).
 *
 * Sent TODAY, before their R6 consumers exist, because extra keys are already usable: a workspace can
 * tag, alert and CRM-sync on "this session came through a VPN" through a Lead Tag rule on
 * `sourceMetadata.fingerprint.transientMetadata.ipIntelligence.isVpn`. That is **five** dot-path
 * segments — exactly their `maxPathDepth` default — so nothing here may nest one level deeper.
 *
 * **False booleans and empty strings are OMITTED, not sent.** Their `exists` operator treats `false`
 * and `""` as present, so `isVpn: false` on every session makes an `exists` rule match every lead.
 * `provider`/`lookedUpAt` are always present when a lookup happened. `provider` names only the providers
 * that ANSWERED (EVAS-147), so a block without verdicts whose `provider` names an anonymity vendor
 * (`proxycheck`, `ipinfo-privacy`, `maxmind-anonymous-mmdb`, `maxmind-insights-api`) means "the vendor
 * looked and found nothing"; one naming only a geo database or `free-lists` means no vendor looked.
 * A `<geo>->ipinfo-lite-api` entry means that geo provider FAILED and the free fallback answered, so
 * `maxmind-insights-api->ipinfo-lite-api` names Insights without it having looked.
 */
export function leadvalIpIntelligence(geo) {
    const a = geo.anonymity;
    return {
        ...(a.isAnonymous ? { isAnonymous: true } : {}),
        ...(a.isVpn ? { isVpn: true } : {}),
        ...(a.isProxy ? { isProxy: true } : {}),
        ...(a.isTor ? { isTor: true } : {}),
        ...(a.isRelay ? { isRelay: true } : {}),
        ...(a.isHosting ? { isHosting: true } : {}),
        ...(a.isResidentialProxy ? { isResidentialProxy: true } : {}),
        ...(a.serviceName ? { serviceName: a.serviceName } : {}),
        ...(a.confidence !== undefined ? { confidence: a.confidence } : {}),
        ...(a.riskScore !== undefined ? { riskScore: a.riskScore } : {}),
        ...(a.sources.length > 0 ? { sources: a.sources } : {}),
        provider: geo.provider,
        lookedUpAt: geo.lookedUpAt,
    };
}
/**
 * Merge the server-observed IP intelligence into the browser's fingerprint (§7.1 + report 12 items 1–2).
 *
 * The collector deliberately makes no network call, so `transientMetadata.ipAddress` — one of the six
 * fields their minimum-fingerprint check requires — can only be filled here, from the address the join
 * function observed on the request the browser actually made to us. `ipCountryCode` matters more than it
 * looks: it is the third rung of the pricing-country ladder and can move a lead from
 * `NO_BASE_VALUE_FOR_COUNTRY` (unpriced, unsellable) to priced. Their `georesolver.countryCode` is NOT
 * read by that ladder — two near-identical key names, one of which is money (R7.5).
 *
 * Everything the browser produced is otherwise forwarded UNMODIFIED (§7): hashes stay hashes, and a
 * probe that never ran stays absent rather than becoming `null`.
 */
export function enrichFingerprintForLeadval(fingerprint, ctx) {
    const transient = { ...(fingerprint.transientMetadata ?? {}) };
    const ip = ctx.clientIp ?? ctx.geo?.ip;
    if (ip)
        transient.ipAddress = ip;
    if (ctx.geo?.geo.country)
        transient.ipCountryCode = ctx.geo.geo.country;
    if (ctx.geo)
        transient.ipIntelligence = leadvalIpIntelligence(ctx.geo);
    return {
        ...fingerprint,
        transientMetadata: transient,
        ...(ctx.geo ? { georesolver: leadvalGeoresolver(ctx.geo) } : {}),
    };
}
/**
 * Assemble `metadata`, stringifying every structured value EXACTLY ONCE (§3.1).
 *
 * Never re-stringify: a doubly-encoded value is recorded as a conformance violation and MAY fail to
 * parse entirely, discarding the evidence it carries. `processor` goes out as a native number (their
 * registry declares it a `number`; a stringified integer is accepted too, but there is no reason to
 * make them unwrap it).
 *
 * Omission is meaningful and deliberate: `prompt` (we assemble instruction strings, not versioned
 * prompt ids — sending a fabricated id would be provenance we do not have), `embContext`/`embProfile`
 * (operator-authored placement descriptions; no field for them in our data model yet), `experiment`,
 * `segments`/`lastDuration` (we do not count reconnect segments), and `consent.recordingDisclosed` /
 * `disclosureTextVersion` (we have no versioned disclosure text). §3 is explicit: omit unknown values,
 * never fake them.
 */
export function buildLeadvalMetadata(input) {
    const metadata = {
        leadvalSpecVersion: LEADVAL_SPEC_VERSION,
        sessionId: input.sessionId,
        processor: input.processor,
        started: input.started,
        embedding: stringifyOnce({
            embeddingId: input.embedding.embeddingId,
            room: input.embedding.room,
            ...(input.embedding.libraryVersion ? { libraryVersion: input.embedding.libraryVersion } : {}),
            ...pageContextFields(input.embedding.pageContext),
        }),
        agent: stringifyOnce(input.agent),
    };
    if (input.speakers.length > 0)
        metadata.speakers = stringifyOnce(input.speakers);
    if (input.fingerprint)
        metadata.fingerprint = stringifyOnce(input.fingerprint);
    if (input.tools && input.tools.length > 0) {
        metadata.function = stringifyOnce({ tools: input.tools });
    }
    if (input.contactConsent) {
        metadata.consent = stringifyOnce({ contactConsent: input.contactConsent });
    }
    if (input.ended)
        metadata.ended = input.ended;
    if (input.endReason)
        metadata.endReason = input.endReason;
    if (input.totalDurationS !== undefined) {
        metadata.totalDuration = `${Math.max(0, Math.round(input.totalDurationS))}s`;
    }
    if (input.evas)
        metadata.evas = stringifyOnce(input.evas);
    return metadata;
}
/**
 * `utm` keys as LeadVal names them: **`source`, `medium`, `campaign`, `term`, `content`** — NOT the
 * `utm_`-prefixed query-parameter names (§6.6, and their example `{"source":"google","medium":"cpc"}`).
 *
 * Measured on dev 2026-09-04: we were sending `utm.utm_source`, because the widget's collector stores the
 * raw query key (`packages/widget-core/src/page-context.ts`). Nothing complains — an unrecognised sub-key
 * inside a structured metadata value is not graded, so no conformance note fires — and the cost is silent:
 * a workspace's attribution card and any Custom Signal mapped at `embedding.utm.source` find nothing.
 *
 * Normalising HERE rather than in the collector is deliberate: this is the boundary that owns their
 * contract, it is idempotent (an already-bare `source` passes through), and it repairs the `PageContext`
 * blocks already persisted on `Session.visitorContext` with prefixed keys. Non-canonical `utm_*` params
 * (`utm_id`, `utm_source_platform`) keep their information and lose only the prefix — dropping a campaign
 * value would be worse than carrying one they ignore.
 */
function leadvalUtmKeys(utm) {
    const out = {};
    for (const [key, value] of Object.entries(utm)) {
        const canonical = key.toLowerCase().replace(/^utm_/, "");
        if (canonical.length > 0)
            out[canonical] = value;
    }
    return out;
}
/** `pageUrl`/`referrer`/`utm`/`clickIds` — attribution captured by the embed script at page load, which
 * is the authoritative channel (§6.6). Empty objects are dropped: an empty `utm` is not evidence.
 *
 * ⚠ Absence here is NOT evidence of a broken collector: content blockers strip `utm_*` and `gclid` before
 * any page script runs (measured — report 02 §10). A non-empty `pageUrl` beside a missing `utm` is the
 * signature of a blocked browser. */
function pageContextFields(pageContext) {
    if (!pageContext)
        return {};
    const utm = pageContext.utm && Object.keys(pageContext.utm).length > 0
        ? leadvalUtmKeys(pageContext.utm)
        : undefined;
    const clickIds = pageContext.clickIds && Object.keys(pageContext.clickIds).length > 0
        ? pageContext.clickIds
        : undefined;
    return {
        ...(pageContext.pageUrl ? { pageUrl: pageContext.pageUrl } : {}),
        ...(pageContext.referrer ? { referrer: pageContext.referrer } : {}),
        ...(utm ? { utm } : {}),
        ...(clickIds ? { clickIds } : {}),
    };
}
/** The single-stringification rule, in one place so no call site can double-encode (§3.1). */
function stringifyOnce(value) {
    return JSON.stringify(value);
}
/**
 * Which `MANDATORY` metadata keys this document is missing — our own pre-send check.
 *
 * Worth the few lines because the failure it catches is unrecoverable: on a full vertical (processor 2
 * or 3) a document missing one of these creates no lead and cannot be repaired afterwards; the session
 * must be re-run. It also catches the §6.10 half of the rule — a key that is *present* but empty,
 * `null`, or an empty JSON object fails the type/shape check exactly as absence does — which is the
 * shape a later chunk overwriting an earlier good value would produce (§4).
 */
export function missingMandatoryMetadataKeys(metadata) {
    return LEADVAL_MANDATORY_METADATA_KEYS.filter((key) => {
        const value = metadata[key];
        if (value === undefined || value === null)
            return true;
        if (typeof value === "number")
            return false; // 0 is legitimate for `processor` and NOT absent
        if (typeof value === "boolean")
            return false;
        const trimmed = value.trim();
        return trimmed.length === 0 || trimmed === "{}" || trimmed === "null";
    });
}
/**
 * Keys LeadVal's LIVE registry marks MANDATORY that {@link LEADVAL_MANDATORY_METADATA_KEYS} — our belief
 * about that registry — does not know about. **Non-empty means our writer is out of date and does not
 * know it.**
 *
 * This is the whole reason we asked them for `rejection.mandatoryKeys` (Round-14): the set lives in a
 * Firestore document (`/global_calibration/sla_signals`), so it can widen with **no deploy and no revision
 * to gate on**. `processor` did exactly that on 2026-08-26 and silently rejected every session until
 * somebody read their commits. Comparing the two sets turns that into a log line the first time any
 * session is rejected — the rejection is lost either way, but the *next* thousand are not.
 *
 * Deliberately ONE-DIRECTIONAL. A key of ours that is absent from their set means we over-send, which is
 * harmless and is the **current expected state**: measured 2026-09-04 their set is
 * `["started","embedding","processor"]` while ours also carries `sessionId`, because `sessionId` rides the
 * envelope and is enforced by `CUSTOMID_SESSIONID_MISMATCH` instead. Warning on that direction would fire
 * on every rejection and teach the reader to ignore it.
 *
 * An EMPTY or absent `liveKeys` is "not recorded" (rejections predating 2026-09-03), never "nothing is
 * mandatory" — it returns `[]` rather than claiming the whole registry is unknown.
 */
export function unknownMandatoryMetadataKeys(liveKeys) {
    if (!liveKeys || liveKeys.length === 0)
        return [];
    const known = new Set(LEADVAL_MANDATORY_METADATA_KEYS);
    return liveKeys.filter((key) => !known.has(key));
}
/**
 * Assemble the `content` a store-written document would render, for TESTS and read-back verification.
 *
 * The writer never builds this string — it POSTs structured messages and the store assembles the text
 * (§5's preamble: "you never type a single bracket"). But the grammar is what LeadVal parses, so our
 * tests assert the messages we send render to the exact shapes in their §14 fixture. Faithful to the
 * store's measured behaviour: a tool result renders `[TOOL (name)]` like the call, with no
 * `[TOOL RESPONSE …]` header, no `id:` annotation and no timestamp — none of the three is producible.
 */
export function renderLeadvalTranscript(messages) {
    return messages
        .map((m) => {
        const role = m.role === "tool" ? "TOOL" : m.role.toUpperCase();
        // A nameless message renders as a bare `[USER]` — no parentheses at all (§5.6). We never send one
        // (that turn would be withheld), but the renderer models the store, not our intentions.
        const header = m.name ? `[${role} (${m.name})]` : `[${role}]`;
        return `${header}\n${m.content}`;
    })
        .join(LEADVAL_TURN_SEPARATOR);
}
//# sourceMappingURL=leadval-ingestion.js.map